import { type Component, createEffect, onCleanup } from 'solid-js';
import styles from './PrePermissionNudge.module.css';

export interface PrePermissionNudgeProps {
  /** Called when the user clicks "Allow Access" — parent should invoke getUserMedia */
  onAllow: () => void;
  /** Called when the user clicks "Learn More" */
  onLearnMore?: () => void;
}

/**
 * Pre-permission nudge overlay.
 * Appears BEFORE the browser permission dialog to explain why
 * camera and microphone access is needed and reassure the user.
 */
const PrePermissionNudge: Component<PrePermissionNudgeProps> = (props) => {
  // Close on Escape — treat as dismissal (don't proceed)
  createEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Do nothing — user must make a choice
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    onCleanup(() => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    });
  });

  return (
    <div class={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="nudge-heading">
      <div class={styles.card}>
        {/* Friendly camera/mic icon */}
        <div class={styles.icon} aria-hidden="true">
          <span role="img" aria-label="Camera and microphone">&#127909;</span>
        </div>

        <h2 id="nudge-heading" class={styles.heading}>
          Camera &amp; Microphone Access
        </h2>

        <p class={styles.description}>
          To start chatting, we need access to your camera and microphone.
        </p>

        {/* Reassurance callout */}
        <div class={styles.reassurance}>
          <span class={styles.reassuranceIcon} aria-hidden="true">&#128274;</span>
          <p class={styles.reassuranceText}>
            Your face will be hidden with a fun filter until you choose to reveal it.
            Your privacy is our priority.
          </p>
        </div>

        {/* Actions */}
        <div class={styles.actions}>
          <button
            class={`btn btn-primary btn-lg ${styles.allowButton}`}
            onClick={() => props.onAllow()}
            type="button"
          >
            Allow Access
          </button>

          <button
            class={styles.learnMore}
            onClick={() => props.onLearnMore?.()}
            type="button"
          >
            Learn more about privacy
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrePermissionNudge;
